/**
 * The filters, as chips.
 *
 * Chips rather than selects because the interesting question on a shelf is "what
 * is in here", and a select hides the answer behind a click. The counts come from
 * `/api/facets` and are part of the answer: a genre with three books and a genre
 * with sixty deserve different attention.
 *
 * Every row disappears when the library has nothing to put in it. A fresh import
 * with no shelves should not be shown an empty Shelf heading and wonder what it
 * did wrong.
 *
 * On a phone the same chips would be a wall: four rows of them push every cover
 * below the fold, and the shelf is what the visit was for. So under `lg` the
 * whole thing folds into one control that says how many filters are on, and the
 * chips move into a bottom sheet. Nothing about the desktop bar changes.
 */

import { useEffect, useState } from "react";

import { Chip } from "../components/ui";
import { CloseIcon } from "../components/icons";
import { type Key, langLabel, plural, t, useUiLang } from "../i18n";
import { useScrollLock } from "../lib/useScrollLock";
import type { Facet, Facets, ReadingStatus } from "../lib/types";
import { genresForShelf, statusCount, type Filters, type SortKey } from "./useLibrary";

/**
 * The tables hold keys rather than words, and the word is looked up at render
 * time. A module level string would be resolved once, when the file is first
 * imported, and would then stay in whatever language the interface opened in.
 */
const STATUS_KEYS: Record<ReadingStatus, Key> = {
  reading: "filterReading",
  finished: "filterFinished",
  unread: "filterUnread",
};

const SORT_KEYS: Record<SortKey, Key> = {
  author: "sortAuthor",
  title: "sortTitle",
  recent: "sortRecent",
  progress: "sortProgress",
};

/**
 * A labelled row of chips. Inline on a wide screen, where the label is a column
 * of its own; stacked in the sheet, where 56px of label would leave a phone with
 * two chips a line.
 */
function Row({
  label,
  stacked,
  children,
}: {
  label: string;
  stacked?: boolean;
  children: React.ReactNode;
}) {
  if (stacked) {
    return (
      <div className="flex flex-col gap-2">
        <span className="text-2xs font-medium uppercase tracking-[0.14em] text-[var(--color-faint)]">
          {label}
        </span>
        <div className="flex flex-wrap gap-2">{children}</div>
      </div>
    );
  }
  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-2">
      <span className="w-14 shrink-0 text-2xs font-medium uppercase tracking-[0.14em] text-[var(--color-faint)]">
        {label}
      </span>
      {children}
    </div>
  );
}

/** A chip that names a facet and shows how many books it holds. */
function FacetChip({
  facet,
  label,
  active,
  onToggle,
}: {
  facet: Facet;
  label?: string;
  active: boolean;
  onToggle: () => void;
}) {
  return (
    <Chip active={active} onClick={onToggle}>
      <span className="inline-flex items-center gap-1.5">
        {label ?? facet.name}
        <span className={active ? "opacity-70" : "text-[var(--color-faint)]"}>{facet.count}</span>
      </span>
    </Chip>
  );
}

/**
 * A status chip. The count is optional because the server may not have answered
 * yet, and a chip that reads "Read 0" for a second while the facets load is a
 * chip that lies for a second.
 */
function StatusChip({
  label,
  count,
  active,
  onClick,
}: {
  label: string;
  count: number | null;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <Chip active={active} onClick={onClick}>
      <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
        {label}
        {count !== null && (
          <span className={active ? "opacity-70" : "text-[var(--color-faint)]"}>{count}</span>
        )}
      </span>
    </Chip>
  );
}

/** One active filter, with the means to drop just that one. */
function ActivePill({ text, onRemove }: { text: string; onRemove: () => void }) {
  useUiLang();
  return (
    <span className="inline-flex items-center gap-1 rounded-full border border-[var(--color-accent)] bg-[var(--color-accent-soft)] py-1 pl-3 pr-1 text-xs text-[var(--color-accent)] coarse:min-h-11 coarse:pl-4">
      {text}
      <button
        onClick={onRemove}
        aria-label={t("filterRemove", { text })}
        title={t("filterRemove", { text })}
        className="tap inline-flex h-5 w-5 items-center justify-center rounded-full transition-colors duration-150 hover:bg-white/15"
      >
        <CloseIcon className="h-3 w-3" />
      </button>
    </span>
  );
}

function FunnelIcon() {
  return (
    <svg
      viewBox="0 0 20 20"
      className="h-4 w-4 shrink-0"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M3 4.5h14l-5.4 6.3v4.9l-3.2 1.8v-6.7z" />
    </svg>
  );
}

export function FilterBar({
  filters,
  facets,
  total,
  onChange,
  onReset,
}: {
  filters: Filters;
  facets: Facets | null;
  total: number;
  onChange: (part: Partial<Filters>) => void;
  onReset: () => void;
}) {
  useUiLang();
  const [sheet, setSheet] = useState(false);
  useScrollLock(sheet);

  useEffect(() => {
    if (!sheet) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setSheet(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [sheet]);

  const shelves = facets?.shelves ?? [];
  const langs = facets?.langs ?? [];
  const genres = genresForShelf(facets, filters.shelf);

  const active: { key: string; text: string; clear: Partial<Filters> }[] = [];
  if (filters.q) active.push({ key: "q", text: `"${filters.q}"`, clear: { q: "" } });
  if (filters.shelf) active.push({ key: "shelf", text: filters.shelf, clear: { shelf: "", genre: "" } });
  if (filters.genre) active.push({ key: "genre", text: filters.genre, clear: { genre: "" } });
  if (filters.lang)
    active.push({ key: "lang", text: langLabel(filters.lang), clear: { lang: "" } });
  if (filters.status)
    active.push({ key: "status", text: t(STATUS_KEYS[filters.status]), clear: { status: "" } });

  /**
   * Read, reading, unread: the one filter that answers "where am I with this
   * library". It carries its count from `/api/facets`, and it is the only row
   * that also shows outside the sheet on a phone, because hiding "12 read"
   * behind a Filters button is hiding the answer behind the question.
   */
  const statusChips = (
    <>
      <StatusChip
        label={t("filterAll")}
        count={statusCount(facets, "")}
        active={filters.status === ""}
        onClick={() => onChange({ status: "" })}
      />
      {(Object.keys(STATUS_KEYS) as ReadingStatus[]).map((status) => (
        <StatusChip
          key={status}
          label={t(STATUS_KEYS[status])}
          count={statusCount(facets, status)}
          active={filters.status === status}
          onClick={() => onChange({ status: filters.status === status ? "" : status })}
        />
      ))}
    </>
  );

  // The one set of chips, laid out twice: inline in the desktop bar, stacked in
  // the phone sheet. Written once so the two can never drift apart.
  const rows = (stacked: boolean) => (
    <>
      <Row label={t("filterStatus")} stacked={stacked}>
        {statusChips}
      </Row>

      {langs.length > 1 && (
        <Row label={t("filterLanguage")} stacked={stacked}>
          {langs.map((lang) => (
            <FacetChip
              key={lang.name}
              facet={lang}
              label={langLabel(lang.name)}
              active={filters.lang === lang.name}
              onToggle={() => onChange({ lang: filters.lang === lang.name ? "" : lang.name })}
            />
          ))}
        </Row>
      )}

      {shelves.length > 0 && (
        <Row label={t("filterShelf")} stacked={stacked}>
          {shelves.map((shelf) => (
            <FacetChip
              key={shelf.name}
              facet={shelf}
              active={filters.shelf === shelf.name}
              // Leaving a shelf leaves its genres behind too, otherwise the grid
              // silently keeps a genre the new shelf has never heard of.
              onToggle={() =>
                onChange(
                  filters.shelf === shelf.name
                    ? { shelf: "", genre: "" }
                    : { shelf: shelf.name, genre: "" },
                )
              }
            />
          ))}
        </Row>
      )}

      {genres.length > 0 && (
        <Row label={t("filterGenre")} stacked={stacked}>
          {genres.map((genre) => (
            <FacetChip
              key={`${genre.shelf ?? ""}/${genre.name}`}
              facet={genre}
              active={filters.genre === genre.name}
              onToggle={() => onChange({ genre: filters.genre === genre.name ? "" : genre.name })}
            />
          ))}
        </Row>
      )}

      <Row label={t("filterSort")} stacked={stacked}>
        {(Object.keys(SORT_KEYS) as SortKey[]).map((sort) => (
          <Chip key={sort} active={filters.sort === sort} onClick={() => onChange({ sort })}>
            {t(SORT_KEYS[sort])}
          </Chip>
        ))}
      </Row>
    </>
  );

  const strip = active.length > 0 && (
    <div className="flex flex-wrap items-center gap-2 border-t border-[var(--color-line)] pt-3">
      <span className="text-2xs text-[var(--color-muted)]">
        {plural(total, "libBookOne", "libBookMany")}
      </span>
      {active.map((item) => (
        <ActivePill key={item.key} text={item.text} onRemove={() => onChange(item.clear)} />
      ))}
      <button
        onClick={onReset}
        className="tap rounded px-1 text-2xs text-[var(--color-muted)] underline decoration-[var(--color-line)] underline-offset-4 transition-colors duration-150 hover:text-[var(--color-ink)]"
      >
        {t("filterClearAll")}
      </button>
    </div>
  );

  return (
    <>
      {/* Phone and tablet: one line, so the first cover is on screen at once.
          The Filters button, then the status chips, which scroll sideways rather
          than wrap: a second line here would push the shelf off the screen. */}
      <div className="-mx-4 flex items-center gap-2 overflow-x-auto px-4 pb-1 [-ms-overflow-style:none] [scrollbar-width:none] [&>*]:shrink-0 [&::-webkit-scrollbar]:hidden lg:hidden">
        <button
          onClick={() => setSheet(true)}
          aria-haspopup="dialog"
          aria-expanded={sheet}
          className={
            "tap inline-flex items-center gap-2 rounded-lg border px-3.5 py-2 text-sm " +
            "transition-colors duration-150 " +
            (active.length > 0
              ? "border-[var(--color-accent)] bg-[var(--color-accent-soft)] text-[var(--color-accent)]"
              : "border-[var(--color-line)] text-[var(--color-muted)]")
          }
        >
          <FunnelIcon />
          {t("filterTitle")}
          {active.length > 0 && (
            <span
              aria-label={plural(active.length, "filterActiveOne", "filterActiveMany")}
              className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-[var(--color-accent)] px-1.5 text-2xs font-medium text-[var(--color-on-accent)]"
            >
              {active.length}
            </span>
          )}
        </button>
        {/* A hairline, so the chips read as a second group rather than as more
            buttons of the same kind. */}
        <span className="h-5 w-px shrink-0 bg-[var(--color-line)]" aria-hidden="true" />
        {statusChips}
        {/* No count here: the Library heading two lines above already shows the
            same number, filtered or not, and repeating it reads as a bug. The
            clearing button is in the sheet, at the bottom, next to the chips it
            clears. */}
      </div>

      {/* Desktop: the bar as it always was. */}
      <div className="hidden flex-col gap-3 lg:flex">
        {rows(false)}
        {strip}
      </div>

      {sheet && (
        <div className="fixed inset-0 z-50 lg:hidden" data-testid="filter-sheet">
          <div
            aria-hidden="true"
            onClick={() => setSheet(false)}
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
          />
          <div
            role="dialog"
            aria-modal="true"
            aria-label={t("filterTitle")}
            className={
              "absolute inset-x-0 bottom-0 flex max-h-[85vh] flex-col rounded-t-2xl " +
              "bg-[var(--color-surface)] ring-1 ring-[var(--color-line)] " +
              "pb-[env(safe-area-inset-bottom)] shadow-2xl shadow-black/60"
            }
          >
            <div className="flex items-center justify-between px-4 pb-2 pt-3">
              <h2 className="text-sm font-medium text-[var(--color-ink)]">{t("filterTitle")}</h2>
              <button
                onClick={() => setSheet(false)}
                aria-label={t("filterClose")}
                className="tap inline-flex h-9 w-9 items-center justify-center rounded-lg text-[var(--color-muted)] transition-colors duration-150 hover:bg-[var(--color-raised)] hover:text-[var(--color-ink)]"
              >
                <CloseIcon className="h-4 w-4" />
              </button>
            </div>

            <div className="flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto overscroll-contain px-4 py-3">
              {rows(true)}
            </div>

            <div className="flex items-center gap-3 border-t border-[var(--color-line)] px-4 py-3">
              <button
                onClick={onReset}
                disabled={active.length === 0}
                className="tap rounded-lg px-3 py-2 text-sm text-[var(--color-muted)] transition-colors duration-150 disabled:opacity-40"
              >
                {t("filterClearAll")}
              </button>
              <button
                onClick={() => setSheet(false)}
                className="tap ml-auto rounded-lg bg-[var(--color-accent)] px-5 py-2 text-sm font-medium text-[var(--color-on-accent)]"
              >
                {plural(total, "filterShowOne", "filterShowMany")}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
